import { useMsal } from '@azure/msal-react';
import { Button, Text } from '@fluentui/react-components';
import { AddCircleRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { ReactElement, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { StatusResponse } from '../../api';
import { downloadFile } from '../../common/file/download-file';
import { useStores, useTitle } from '../../common/hooks';
import { UserInfo } from '../../models';
import { ResourceTicketRequest, ResourceTypes } from '../../models/resource-services';
import { NominationRouteParams } from '../../models/route-params';
import { NominatePageRouteStates } from '../../models/route-states';
import { RoutePaths } from '../../route-paths';
import { StepNav, StepperSkeleton, StepRail, StepRailStatus } from '../../shared/stepper';
import { useAppToast } from '../../shared/toast';
import { FlexBox } from '../shared-components/flex-box';
import Loading from '../shared-components/Loading';
import { AdditionalContactSection } from './components/AdditionalContactSection';
import { CompanyInformationSection } from './components/CompanyInformationSection';
import { ConfirmPartnerIdModal } from './components/ConfirmPartnerIdModal';
import { JointNomineesSection } from './components/JointNomineesSection';
import { NominationSubmissionSection } from './components/NominationSubmissionSection';
import { NominationWizardSection } from './components/NominationWizardSection';
import { YourInformationSection } from './components/YourInformationSection';
import {
  nominationStepper,
  NominationStepperInstance,
  NominationStepId,
} from './nomination-stepper';
import './NominationWizardPage.scss';

const YOUR_INFORMATION_DESCRIPTION = 'Provide your contact information as the primary owner of this nomination.';
const ADDITIONAL_CONTACT_DESCRIPTION = 'In the event your company is named a winner or finalist, you can provide a secondary contact to be included on the notification. This individual will also be able to accept the award on behalf of the company';
const JOINT_NOMINEE_DESCRIPTION = "This section is only applicable for joint submission entries (2 or more companies submitting a nomination together). You may add up to (3) additional companies' information. NOTE: Each company must meet the requirements.";

interface NominationWizardPageProps {
  userInfo: UserInfo;
}

const NominationWizardPageImpl: React.FC<NominationWizardPageProps> = (props) => {
  const {
    userInfo,
  } = props;

  const {
    instance
  } = useMsal();

  const [hasSelectedAward, setHasSelectedAward] = useState<boolean>(true);

  const { showToast } = useAppToast();


  const { nominationId } = useParams<NominationRouteParams>();

  const location = useLocation();
  const routeState = location.state as NominatePageRouteStates | undefined;

  const navigate = useNavigate();

  const {
    apiStore,
    countryStore,
    nominationWizardPageUiStore,
    partnerTypeStore,
    userStore,
  } = useStores();

  const { nomination } = nominationWizardPageUiStore;

  const allStepIds = nominationStepper.steps.map(step => step.id);

  const getUserMpnIds = async (): Promise<void> => {
    if (!userStore.userInfo?.userId) return;
    try {
      const mpnIds = await apiStore.getCurrentUserPartnerIds(instance, userStore.userInfo.userId);
      userStore.setUserMpnIds(mpnIds);
    } catch (error) {
      // TODO - handle error
      userStore.setUserMpnIds([]);
    }
  };

  const getAwardById = async (awardId: number): Promise<void> => {
    try {
      const award = await apiStore.getAwardById(instance, awardId);
      nominationWizardPageUiStore.setSelectedAward(award);
    } catch (error) {
      // TODO - handle error
    }
  };

  const preloadData = async (): Promise<void> => {
    nominationWizardPageUiStore.setLoadingType('load');
    try {
      await loadNominationProgress();
    } finally {
      nominationWizardPageUiStore.setLoadingType(undefined);
    }
  };

  const loadNominationProgress = async (): Promise<void> => {
    nominationWizardPageUiStore.createNomination();

    const na = countryStore.awardCountries.find((country) => country.countryCode === 'N/A');

    await getUserMpnIds();

    if (nominationId) {
      const nominationIdNumber = Number(nominationId);
      const [nomination, nominationProgress] = await Promise.all([
        apiStore.getNominationById(instance, nominationIdNumber),
        apiStore.getNominationProgress(instance, nominationIdNumber),
      ]);

      if (nomination.status === 'Submitted') {
        navigate(RoutePaths.Home);
        return;
      }

      nominationWizardPageUiStore.createNominationForUpdate(nomination);
      await getAwardById(nomination.award.awardId);
      setHasSelectedAward(nomination.award.awardId > 0);
      nominationWizardPageUiStore.createAssociatedCountryRequestBodyForUpdate(nomination.associatedCountries, na);

      nominationWizardPageUiStore.setNominationSubmissionDescription(
        nomination.award.requireWrittenNomination ?
          'Choose the country association for your nomination, then enter your nomination details in an executive summary format following the guidance here. Add up to three supporting documents, any supporting links should go in these documents or your execuitve summary. Preview your submission as a PDF.  When final, click Review & Submit.' :
          'Please download nomination template and follow instructions'
      );

      if (nomination.award.requireWrittenNomination) {
        await getWrittenExecutiveSummary(nomination.nominationId);
      } else {
        await getExecutiveSummaryDocumentMetadata(nomination.nominationId);
      }

      if (nominationProgress) {
        const furthestStepId = nominationStepper.parseStep(nominationProgress.furthestNominationStepCode) ?? 'your-information';
        nominationWizardPageUiStore.setFurthestVisitedStepId(furthestStepId);
        await validateCompletedSteps(allStepIds, furthestStepId);
        nominationWizardPageUiStore.setInitialStepId(nominationStepper.parseStep(nominationProgress.latestVisitedNominationStepCode) ?? 'your-information');
      }
    } else {
      if (routeState?.selectedAwardId) {
        await getAwardById(routeState.selectedAwardId);
        setHasSelectedAward(routeState.selectedAwardId > 0);
        nominationWizardPageUiStore.setNominationAward();
      } else {
        setHasSelectedAward(false);
      }

      nominationWizardPageUiStore.createAssociatedCountryRequestBodyForUpdate([], na);
      nominationWizardPageUiStore.setNominationOwnerFromUserInfo(userInfo);
      nominationWizardPageUiStore.setNominationOrganizationUserInfo(userInfo);
    }
  }

  const validateStep = (stepId: NominationStepId): boolean => {
    return nominationWizardPageUiStore.isStepValid(
      stepId,
      partnerTypeStore.otherPartnerTypeId,
    );
  };

  const markCompletedStepsValidated = (stepIds: NominationStepId[], furthestStepId: NominationStepId): NominationStepId[] => {
    const furthestStepIndex = stepIds.findIndex(stepId => stepId === furthestStepId);
    const completedStepIds = stepIds.slice(0, furthestStepIndex + 1);
    nominationWizardPageUiStore.markStepsValidated(completedStepIds);
    return completedStepIds;
  };

  const validateCompletedSteps = async (stepIds: NominationStepId[], furthestStepId: NominationStepId): Promise<boolean> => {
    const completedStepIds = markCompletedStepsValidated(stepIds, furthestStepId);
    const validationInput = nominationWizardPageUiStore.getValidationInput(
      partnerTypeStore.otherPartnerTypeId,
    );

    await Promise.all(
      completedStepIds.map(stepId => nominationStepper.validate(stepId, validationInput)),
    );

    return completedStepIds.every(validateStep);
  }

  const createOrUpdateNomination = async (): Promise<void> => {
    return !nomination.nominationId ?
      apiStore
        .createNomination(instance, nomination)
        .then(createdNominationRef => {
          nomination.nominationId = createdNominationRef.nominationId;
          nomination.nominationNumber = createdNominationRef.nominationNumber;
          nomination.name = createdNominationRef.name;
          return apiStore.updateNomination(instance, nomination);
        }) :
      apiStore.updateNomination(instance, nomination);
  }

  const createOrUpdateOrganization = async (): Promise<void> => {
    const { nominationId } = nomination;
    const organization = nominationWizardPageUiStore.nominationOrganization;

    const putOrganizationPromise = apiStore
      .updateNominationOrganization(instance, nominationId, organization)
      .then(organizationRef => {
        organization.organizationId = organizationRef.organizationId;
      });

    await Promise.all(
      [
        putOrganizationPromise,
        apiStore.updateNominationCountries(instance, nomination.nominationId, nominationWizardPageUiStore.associatedCountryIds),
        apiStore.updateNominationPartnerTypes(instance, nomination.nominationId, nomination.partnerTypes),
        apiStore.updateNominationMarketplaceOffer(instance, nomination.nominationId, nomination.marketplaceOfferUrl),
      ]);
  }

  const createOrUpdateContact = async (): Promise<void> => {
    const { nominationId, contacts } = nomination;
    const toBeDeletedContactIds = [...nominationWizardPageUiStore.deletedContactIds];
    await Promise
      .all(toBeDeletedContactIds.map(deletedContactId => apiStore
        .deleteNominationContact(instance, deletedContactId)
        .then(() => {
          const deletedContactIdIndex = nominationWizardPageUiStore.deletedContactIds.indexOf(deletedContactId);
          nominationWizardPageUiStore.deletedContactIds.splice(deletedContactIdIndex, 1);
        })));

    await Promise
      .all(contacts.map(contact =>
        contact.contactId === 0 ?
          apiStore
            .createNominationContact(instance, nominationId, contact)
            .then(contactRef => {
              contact.contactId = contactRef.contactId;
            }) :
          apiStore
            .updateNominationContact(instance, contact)));
  }

  const saveJointNominees = async (): Promise<void> => {
    const { nominationId, jointNominees } = nomination;
    const toBeDeletedJointNomineeIds = [...nominationWizardPageUiStore.deletedJointNomineeIds];
    return Promise
      .all(toBeDeletedJointNomineeIds.map(deletedJointNomineeId => apiStore
        .deleteJointNominee(instance, nominationId, deletedJointNomineeId)
        .then(() => {
          const deletedJointNomineeIdIndex = nominationWizardPageUiStore.deletedJointNomineeIds.indexOf(deletedJointNomineeId);
          nominationWizardPageUiStore.deletedJointNomineeIds.splice(deletedJointNomineeIdIndex, 1);
        })))
      .then(() => Promise
        .all(jointNominees.map(nominee =>
          !nominee.organizationId ?
            apiStore
              .createJointNominee(instance, nominationId, nominee)
              .then(organizationRef => {
                nominee.organizationId = organizationRef.organizationId;
              }) :
            apiStore
              .updateOrganization(instance, nominee))
        ))
      .then(() => { });
  }

  const saveCustomerReferences = async (): Promise<void> => {
    const { nominationId, customerReferences } = nomination;
    const toBeDeletedCustomerReferenceIds = [...nominationWizardPageUiStore.deletedCustomerReferenceIds];
    return Promise
      .all(toBeDeletedCustomerReferenceIds.map(deletedCustomerReferenceId => apiStore
        .deleteCustomerReference(instance, deletedCustomerReferenceId)
        .then(() => {
          const deletedCustomerReferenceIdIndex = nominationWizardPageUiStore.deletedCustomerReferenceIds.indexOf(deletedCustomerReferenceId);
          nominationWizardPageUiStore.deletedCustomerReferenceIds.splice(deletedCustomerReferenceIdIndex, 1);
        })))
      .then(() => Promise
        .all(customerReferences.map(customerReference => {
          if (customerReference.customerReferenceId === 0) {
            customerReference.nominationId = nominationId;
            return apiStore
              .createCustomerReference(instance, nominationId, customerReference)
              .then(customerReferenceRef => {
                customerReference.customerReferenceId = customerReferenceRef.customerReferenceId;
              });
          }

          return apiStore.updateCustomerReference(instance, customerReference);
        })))
      .then(() => { });
  }

  const saveNominationWebsites = async (): Promise<void> => {
    const { nominationId, websites } = nomination;
    const toBeDeletedNominationWebsitesIds = [...nominationWizardPageUiStore.deletedNominationWebsitesIds];
    return Promise
      .all(toBeDeletedNominationWebsitesIds.map((deletedNominationWebsiteId) => apiStore
        .deleteNominationWebsite(instance, deletedNominationWebsiteId)
        .then(() => {
          const deletedIndex = nominationWizardPageUiStore.deletedNominationWebsitesIds.indexOf(deletedNominationWebsiteId);
          if (deletedIndex >= 0) {
            nominationWizardPageUiStore.deletedNominationWebsitesIds.splice(deletedIndex, 1);
          }
        })))
      .then(() => Promise
        .all(websites.map(website => {
          if (website.websiteId === 0) {
            website.nominationId = nominationId;
            return apiStore
              .createNominationWebsite(instance, nominationId, website)
              .then(nominationWebsiteRef => {
                website.websiteId = nominationWebsiteRef.websiteId;
              });
          }

          return apiStore.updateNominationWebsite(instance, website);
        })))
      .then(() => { });
  }

  const saveNominationSubmission = async (): Promise<void> => {
    if (nomination.award.requireWrittenNomination)
      await updateWrittenExecutiveSummary()

    await Promise.all([
      saveCustomerReferences(),
      saveNominationWebsites(),
    ]);

    await Promise.all([
      apiStore.updateNomination(instance, nomination),
      apiStore.updateNominationAbstract(instance, nomination.nominationId, nomination.abstract),
    ]);
  }

  const download = async (req: ResourceTicketRequest): Promise<void> => {
    try {
      const resourceTicket = await apiStore.postResourceTicket(instance, req);
      downloadFile(resourceTicket.ticketId);
    } catch (error) {
      // TODO - handle error
    }
  }

  const exportNominationToPdf = async (): Promise<void> => {
    nominationWizardPageUiStore.setLoadingType('save');
    try {
      await apiStore.updateNomination(instance, nomination);
      if (nomination.award.requireWrittenNomination) {
        await updateWrittenExecutiveSummary();
      }

      await download({
        resourceType: ResourceTypes.ExportedNomination,
        data: {
          routeData: {
            nominationId: nomination.nominationId,
          },
          queryString: {
            exportType: 'PDF',
          },
        },
      });
    } finally {
      nominationWizardPageUiStore.setLoadingType(undefined);
    }
  };

  const getSaveTask = (stepId: NominationStepId): (() => Promise<void>) | undefined => {
    switch (stepId) {
      case 'your-information': return createOrUpdateNomination;
      case 'company-information': return createOrUpdateOrganization;
      case 'additional-contact': return createOrUpdateContact;
      case 'joint-nominee': return saveJointNominees;
      case 'nomination-submission': return saveNominationSubmission;
      default: return undefined;
    }
  }

  const isWizardFormValid = async (stepId: NominationStepId): Promise<boolean> => {
    return validateCompletedSteps(allStepIds, stepId);
  }

  const isSubmittedNominationValid = async (): Promise<boolean> => {
    if (!nominationWizardPageUiStore.isSubmitted) return true;

    return isWizardFormValid('nomination-submission');
  };

  const updateProgress = async (toStepId: NominationStepId): Promise<void> => {
    const furthestStepId = nominationWizardPageUiStore.furthestVisitedStepId;
    const furthestIndex = allStepIds.indexOf(furthestStepId);
    const toIndex = allStepIds.indexOf(toStepId);
    const newFurthestStepId = toIndex > furthestIndex ? toStepId : furthestStepId;

    nominationWizardPageUiStore.setFurthestVisitedStepId(newFurthestStepId);
    await apiStore.updateNominationProgress(instance, nomination.nominationId, {
      furthestNominationStepCode: newFurthestStepId,
      latestVisitedNominationStepCode: toStepId,
    });
  };

  const commitStep = async (fromStepId: NominationStepId, toStepId: NominationStepId): Promise<void> => {
    const saveTask = getSaveTask(fromStepId);

    nominationWizardPageUiStore.setLoadingType('save');
    try {
      await saveTask?.();
      await updateProgress(toStepId);
    } finally {
      nominationWizardPageUiStore.setLoadingType(undefined);
    }
  };

  const handleBeforeStepChange = async (fromStepId: NominationStepId, toStepId: NominationStepId): Promise<boolean> => {
    const isSubmittedFormValid = await isSubmittedNominationValid();
    if (!isSubmittedFormValid) {
      return false;
    }

    const isForward = allStepIds.indexOf(toStepId) > allStepIds.indexOf(fromStepId);
    const isCompletedStepsValid = await validateCompletedSteps(allStepIds, fromStepId);
    if (isForward && !isCompletedStepsValid) {
      return false;
    }

    await commitStep(fromStepId, toStepId);
    return true;
  };

  const getCompanyInformation = async (accountId: string): Promise<void> => {
    nominationWizardPageUiStore.setLoadingType('save');
    try {
      const companyInformation = await apiStore.getCurrentUserCompanyInformation(instance, accountId);
      userStore.setCompanyInformation(companyInformation);
    } catch (error) {
      // TODO - handle error
    } finally {
      nominationWizardPageUiStore.setLoadingType(undefined);
    }
  };

  const getWrittenExecutiveSummary = async (nominationId: number): Promise<void> => {
    try {
      const content = await apiStore.getWrittenExecutiveSummary(instance, nominationId);
      nominationWizardPageUiStore.setWrittenNomination(content);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getExecutiveSummaryDocumentMetadata = async (nominationId: number): Promise<void> => {
    try {
      const executiveSummaryDocument = await apiStore.getExecutiveSummaryDocumentMetadata(instance, nominationId);
      nominationWizardPageUiStore.setExecutiveSummaryDocument(executiveSummaryDocument);
    } catch (error) {
      // TODO - handle error
    }
  };

  const updateWrittenExecutiveSummary = async (): Promise<void> => {
    if (nominationWizardPageUiStore.writtenNominationContent === undefined) return;
    try {
      await apiStore.updateWrittenExecutiveSummary(instance, nomination.nominationId, nominationWizardPageUiStore.writtenNominationContent);
    } catch (error) {
      showToast({
        title: (error as StatusResponse).message,
        intent: 'error',
      })
      throw error;
    }
  }

  const handleSelectMpnIdInputOption = (showMpnIdSelection: boolean): void => {
    if (showMpnIdSelection && !nominationWizardPageUiStore.nominationOrganization.mpnId) {
      nominationWizardPageUiStore.setCompanyInfoDescription('In order to automatically populate existing Partner ID information, please select an Partner ID number associated with your account. If you are not seeing the ID you need, you can manually input nomination information.');
    } else if (showMpnIdSelection && nominationWizardPageUiStore.nominationOrganization.mpnId) {
      nominationWizardPageUiStore.setCompanyInfoDescription('These company details are maintained in Partner Center. If you see any errors in your company details, please update your Partner Center account. After updating, use the Refresh from Partner Center button to update your profile here.');
    } else {
      nominationWizardPageUiStore.setCompanyInfoDescription("If you don't see your company's Partner ID in the list from Partner Center, please manually enter the Partner ID and your company details here.Please note that this will not update Partner Center information.");
    }
  }

  useTitle('Nomination Process - Create or Edit');

  useEffect(() => {
    preloadData();
    return () => {
      nominationWizardPageUiStore.createNomination();
    };
  }, []);

  const handleAddContact = (): void => {
    nominationWizardPageUiStore.addContact();
  };

  const handleAddJointNominee = (): void => {
    nominationWizardPageUiStore.addJointNominee();
  };

  const resolveStatus = (currentStepId: NominationStepId, stepId: string): StepRailStatus => {
    const parsedStepId = nominationStepper.parseStep(stepId);
    if (!parsedStepId) {
      return 'disabled';
    }

    const currentIndex = allStepIds.indexOf(currentStepId);
    const stepIndex = allStepIds.indexOf(parsedStepId);
    const isBeingValidated = nominationWizardPageUiStore.isBeingValidated(parsedStepId);
    const isValid = validateStep(parsedStepId);

    if (stepIndex === currentIndex) {
      return isBeingValidated && !isValid ? 'current invalid' : 'current';
    }

    if (stepIndex > currentIndex && !isBeingValidated) {
      return 'disabled';
    }

    return isValid ? 'valid' : 'invalid';
  };

  const resolveDescription = (stepId: string): string | undefined => {
    switch (stepId) {
      case 'your-information': return YOUR_INFORMATION_DESCRIPTION;
      case 'company-information': return nominationWizardPageUiStore.companyInfoDescription;
      case 'additional-contact': return ADDITIONAL_CONTACT_DESCRIPTION;
      case 'joint-nominee': return JOINT_NOMINEE_DESCRIPTION;
      case 'nomination-submission': return nominationWizardPageUiStore.nominationSubmissionDescription;
      default: return undefined;
    }
  };

  const renderWizard = (stepper: NominationStepperInstance): ReactElement => {
    const currentStepId = stepper.current.id;
    const currentIndex = allStepIds.indexOf(currentStepId);
    const isLastStep = currentIndex === allStepIds.length - 1;
    const isSubmitted = nominationWizardPageUiStore.isSubmitted;

    const advanceForward = async (): Promise<void> => {
      if (!isLastStep) {
        await stepper.next();
        return;
      }

      const isSubmittedFormValid = await isSubmittedNominationValid();
      if (!isSubmittedFormValid) {
        return;
      }

      const areCompletedStepsValid = await validateCompletedSteps(allStepIds, currentStepId);
      if (!areCompletedStepsValid) {
        return;
      }

      const saveTask = getSaveTask(currentStepId);
      nominationWizardPageUiStore.setLoadingType('save');
      try {
        await saveTask?.();
        await updateProgress(currentStepId);
        navigate(`${RoutePaths.ReviewAndSubmit}/${nomination.nominationId}`, { replace: true });
      } finally {
        nominationWizardPageUiStore.setLoadingType(undefined);
      }
    };

    const handleForward = async (): Promise<void> => {
      if (currentStepId === 'company-information') {
        const organization = nominationWizardPageUiStore.nominationOrganization;
        const hasMatchedAccountMpnId = userStore.userMpnIds.some(userMpnId => organization.mpnId === userMpnId.mpnId);
        const isCompanyValid = validateStep('company-information');
        if (!hasMatchedAccountMpnId && isCompanyValid) {
          nominationWizardPageUiStore.setConfirmPartnerIdModalOpen(true);
          return;
        }
      }

      await advanceForward();
    };

    const handleBack = async (): Promise<void> => {
      if (currentIndex === 0) {
        navigate(RoutePaths.SelectAward, { replace: true });
        return;
      }

      await stepper.prev();
    };

    const handleExit = async (): Promise<void> => {
      const isSubmittedFormValid = await isSubmittedNominationValid();
      if (!isSubmittedFormValid) {
        return;
      }

      const saveTask = getSaveTask(currentStepId);
      nominationWizardPageUiStore.setLoadingType('save');
      try {
        await saveTask?.();
        await updateProgress(currentStepId);
        navigate(RoutePaths.Home, { replace: true });
      } catch (error) {
        showToast({
          title: (error as StatusResponse).message,
          intent: 'error',
        });
      } finally {
        nominationWizardPageUiStore.setLoadingType(undefined);
      }
    };

    const handleSelectStep = async (stepId: string): Promise<void> => {
      const parsedStepId = nominationStepper.parseStep(stepId);
      if (parsedStepId) {
        await stepper.goTo(parsedStepId);
      }
    };

    const handleConfirmPartnerId = async (): Promise<void> => {
      nominationWizardPageUiStore.setConfirmPartnerIdModalOpen(false);
      await advanceForward();
    };

    const forwardText = currentStepId === 'joint-nominee' && nomination.jointNominees.length === 0 ? 'Skip' : 'Continue';
    const isExitDisplayed = currentStepId === 'your-information' ? isSubmitted : true;
    const exitText = isSubmitted ? 'Save changes' : 'Save as draft';
    const lastStepForwardText = isSubmitted ? 'Review changes' : 'Review & submit';

    return (
      <>
        <StepRail
          steps={nominationStepper.steps}
          currentStepId={currentStepId}
          resolveStatus={stepId => resolveStatus(currentStepId, stepId)}
          onSelectStep={handleSelectStep}
          resolveDescription={resolveDescription}
        >
          {
            stepper.match({
              'your-information': () => (
                <YourInformationSection />
              ),
              'company-information': () => (
                <CompanyInformationSection
                  onGetCompanyInformation={getCompanyInformation}
                  onSelectMpnIdInputOption={handleSelectMpnIdInputOption}
                />
              ),
              'additional-contact': () => (
                <AdditionalContactSection />
              ),
              'joint-nominee': () => (
                <JointNomineesSection />
              ),
              'nomination-submission': () => (
                <NominationSubmissionSection
                  exportNominationToPdf={exportNominationToPdf}
                />
              ),
            })
          }
          <StepNav
            backText='Back'
            forwardText={forwardText}
            isExitDisplayed={isExitDisplayed}
            exitText={exitText}
            onBack={handleBack}
            onForward={handleForward}
            onExit={handleExit}
            isLastStep={isLastStep}
            lastStepForwardText={lastStepForwardText}
          >
            {
              stepper.match({
                'your-information': () => null,
                'company-information': () => null,
                'additional-contact': () => (
                  nomination.contacts.length < 3 ?
                    <Button
                      appearance='primary'
                      icon={<AddCircleRegular />}
                      onClick={handleAddContact}
                    >
                      Add contact
                    </Button> :
                    null
                ),
                'joint-nominee': () => (
                  (nomination.award.allowJointNomination && nomination.jointNominees.length < 3) ?
                    <Button
                      appearance='primary'
                      icon={<AddCircleRegular />}
                      onClick={handleAddJointNominee}
                    >
                      Add joint nominee
                    </Button> :
                    null
                ),
                'nomination-submission': () => null,
              })
            }
          </StepNav>
        </StepRail>
        {
          nominationWizardPageUiStore.isConfirmPartnerIdModalOpen &&
          <ConfirmPartnerIdModal
            partnerId={nominationWizardPageUiStore.nominationOrganization.mpnId}
            onClose={() => nominationWizardPageUiStore.setConfirmPartnerIdModalOpen(false)}
            onConfirm={handleConfirmPartnerId}
          />
        }
      </>
    );
  };

  if (!hasSelectedAward) {
    return (
      <main
        className='no-select-award'
      >
        <Text>Sorry, no award is currently selected.</Text>
      </main>
    )
  }

  return (
    <main className='nomination-wizard-page'>
      {nominationWizardPageUiStore.isLoadingOverlayVisible && <Loading />}
      <FlexBox
        className='nomination-wizard-page__header'
        aligns='center'
      >
        <FlexBox
          className='header__sub-title'
          aligns='center'
        >
          <Text
            size={500}
            weight='semibold'
            as='h1'
          >
            Create a nomination
          </Text>
        </FlexBox>
        <FlexBox
          className='header__title'
        >
          <Text
            size={600}
            weight='semibold'
            as='h2'
          >
            {nomination.award.title}
          </Text>
        </FlexBox>
      </FlexBox>
      <div className='nomination-wrapper'>
        {
          nominationWizardPageUiStore.loadingType === 'load' ?
            <StepperSkeleton
              stepCount={allStepIds.length}
            /> :
            <nominationStepper.Provider
              defaultStep={nominationWizardPageUiStore.initialStepId}
              beforeStepChange={(context) => handleBeforeStepChange(context.from.id, context.to.id)}
            >
              <NominationWizardSection
                render={renderWizard}
              />
            </nominationStepper.Provider>
        }
      </div>
    </main>
  );
};

export const NominationWizardPage = observer(NominationWizardPageImpl);
